/**
 * Wrangler env resolution for DeepSpace's multi-environment deploys.
 *
 * Lets a single repo deploy to `<app>.app.space` and `<app>-staging.app.space`
 * (or any other named environment) from one `wrangler.toml`:
 *
 *   name = "hopkins"
 *   [vars]
 *   APP_NAME = "hopkins"
 *
 *   [env.staging]
 *   name = "hopkins-staging"
 *   [env.staging.vars]
 *   APP_NAME = "hopkins-staging"
 *
 * Then `npx deepspace deploy --env staging` reads the override and ships to
 * `hopkins-staging.app.space`. The platform treats it as a fully separate
 * app — its own DO state, OAuth tokens, Stripe products, cron schedule.
 * No platform-side change needed beyond letting the CLI register a second
 * app under the same owner.
 *
 * `CLOUDFLARE_ENV` is the wire format. The Cloudflare Vite plugin honors
 * it natively when it builds the worker bundle + emits `dist/wrangler.json`,
 * so the same env var the SDK CLI passes around is also the one Vite reads.
 * `.dev.vars.<env>` is read by the same plugin during preview/build, so we
 * use the matching naming convention for the user-managed secrets file too.
 */

import { existsSync, readFileSync } from 'node:fs'
import { join } from 'node:path'
import { parse as parseToml } from 'smol-toml'
import { resolveAppName } from '../../server/rooms/app-name'

/** Shape of the bits of wrangler.toml that DeepSpace CLI commands read.
 *  Add an optional field here whenever a new command starts consulting
 *  the config — avoids one-off `WranglerConfig & { … }` casts at
 *  call sites and keeps the canonical type in one place. */
export interface WranglerConfig {
  name?: string
  env?: Record<string, { name?: string } | undefined>
  /** `[assets]` block (`directory`, `binding`, `not_found_handling`,
   *  `run_worker_first`). Consumed by deploy.ts to merge extra
   *  worker-first routes into the deployed metadata. */
  assets?: {
    directory?: string
    binding?: string
    not_found_handling?: string
    run_worker_first?: unknown
  }
  /** `[vars]` block — string-valued environment variables baked into
   *  the worker at deploy time. */
  vars?: Record<string, unknown>
}

/**
 * Resolve the appName that this CLI invocation should target.
 *
 * Without `envName`: returns the top-level `name`.
 * With `envName`:    returns `[env.<envName>].name` and requires that the
 *                    env block exists and declares a name. We refuse to
 *                    fall back to the top-level name because that would
 *                    silently deploy staging code to production.
 *
 * Returns a discriminated union so callers can surface a precise error
 * (matches the existing `resolveAppName` contract).
 */
export function resolveAppNameForEnv(
  config: WranglerConfig,
  envName: string | undefined,
): { ok: true; name: string; warning?: string } | { ok: false; reason: string } {
  if (!envName) {
    return resolveAppName(config.name)
  }
  const envBlock = config.env?.[envName]
  if (!envBlock) {
    return {
      ok: false,
      reason: `wrangler.toml: no [env.${envName}] block found. ` +
        `Add one with a distinct \`name\` to deploy this environment.`,
    }
  }
  if (typeof envBlock.name !== 'string' || envBlock.name.trim() === '') {
    return {
      ok: false,
      reason: `wrangler.toml: [env.${envName}].name is missing. ` +
        `Set a distinct app name (e.g. "${typeof config.name === 'string' ? config.name : 'myapp'}-${envName}") so this environment doesn't overwrite the default.`,
    }
  }
  if (envBlock.name === config.name) {
    return {
      ok: false,
      reason: `wrangler.toml: [env.${envName}].name "${envBlock.name}" is the same as the top-level name. ` +
        `Pick a distinct name (e.g. "${config.name}-${envName}") so deploys don't collide.`,
    }
  }
  return resolveAppName(envBlock.name)
}

/**
 * The .dev.vars filename for an environment. Matches the convention the
 * Cloudflare Vite plugin already uses when `CLOUDFLARE_ENV` is set, so the
 * SDK CLI and the plugin agree on which file holds the per-env secrets.
 */
export function devVarsPathFor(appDir: string, envName: string | undefined): string {
  return envName ? join(appDir, `.dev.vars.${envName}`) : join(appDir, '.dev.vars')
}

/**
 * Read + parse wrangler.toml. Caller checks existence first via
 * `hasWranglerConfig`. Throws a `WranglerConfigError` with the absolute
 * path embedded on a malformed file — saves the user from staring at
 * a raw smol-toml parser stack trace when they have one stray
 * unquoted character in their config.
 */
export class WranglerConfigError extends Error {
  constructor(public readonly path: string, public readonly cause: unknown) {
    const detail = cause instanceof Error ? cause.message : String(cause)
    super(`wrangler.toml: malformed TOML at ${path}: ${detail}`)
    this.name = 'WranglerConfigError'
  }
}

export function readWranglerConfig(appDir: string): WranglerConfig {
  const path = join(appDir, 'wrangler.toml')
  const raw = readFileSync(path, 'utf-8')
  try {
    return parseToml(raw) as WranglerConfig
  } catch (err) {
    throw new WranglerConfigError(path, err)
  }
}

/** Convenience: true when wrangler.toml exists at the standard path. */
export function hasWranglerConfig(appDir: string): boolean {
  return existsSync(join(appDir, 'wrangler.toml'))
}
